import { SpotMarkdown } from "@/components/Spotflow/SpotMarkdown/SpotMarkdown";
import {
  IterationWidthErrorView,
  iterationWidths,
} from "@/components/Spotflow/WorkflowRunner/Outputs/iterationWidthError";
import {
  NoxusJsonView,
  tryParseJsonObject,
} from "@/components/UI/NoxusJsonView";
import {
  SANDBOX_CAPACITY_IDENTIFIER,
  sandboxCapacityMarkdown,
  type SandboxCapacityParams,
} from "@/utils/sandboxCapacityController";

function nonEmptyString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

export function ErrorDetailsView({
  value,
  nodeId,
}: {
  value: unknown;
  /** Owning node — lets errors that ship structured refs resolve them to the
   *  same variable badges the editor shows. */
  nodeId?: string;
}) {
  const data = typeof value === "string" ? tryParseJsonObject(value) : value;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const record = data as Record<string, unknown>;
    const widths = nodeId ? iterationWidths(record) : null;
    if (widths) {
      return (
        <IterationWidthErrorView nodeId={nodeId as string} widths={widths} />
      );
    }
    if (record.identifier === SANDBOX_CAPACITY_IDENTIFIER) {
      return (
        <SpotMarkdown
          markdown={sandboxCapacityMarkdown(
            (record.params ?? {}) as SandboxCapacityParams
          )}
        />
      );
    }
    const markdown = nonEmptyString(record.markdown);
    if (markdown) return <SpotMarkdown markdown={markdown} />;
    const message = nonEmptyString(record.message);
    if (message) {
      const snippet = nonEmptyString(record.snippet);
      return (
        <div className="space-y-2">
          <p className="text-sm text-foreground whitespace-pre-wrap break-words">
            {message}
          </p>
          {snippet && snippet !== message && (
            <pre className="text-xs text-foreground/80 whitespace-pre-wrap break-words font-mono leading-relaxed rounded-md border border-border/40 bg-muted/20 p-3">
              {snippet}
            </pre>
          )}
        </div>
      );
    }
    return <NoxusJsonView data={data as object} expand={3} />;
  }
  const fallback: object | unknown[] = Array.isArray(data)
    ? data
    : { error: value };
  return <NoxusJsonView data={fallback} expand={3} />;
}
